const PAGE_SIZE_KEY = "ai-support-monitor.pageSize";
const UI_ZOOM_KEY = "ai-support-monitor.uiZoom";

export const PAGE_SIZE_OPTIONS = [10, 50, 100] as const;
export type PageSizeOption = (typeof PAGE_SIZE_OPTIONS)[number];

export const UI_ZOOM_DEFAULT = 1;
export const UI_ZOOM_MIN = 0.5;
export const UI_ZOOM_MAX = 3;
export const UI_ZOOM_STEP = 0.1;

export function isPageSizeOption(value: number): value is PageSizeOption {
  return (PAGE_SIZE_OPTIONS as readonly number[]).includes(value);
}

export function readStoredPageSize(fallback: PageSizeOption = 10): PageSizeOption {
  try {
    const raw = Number(localStorage.getItem(PAGE_SIZE_KEY));
    return isPageSizeOption(raw) ? raw : fallback;
  } catch {
    return fallback;
  }
}

export function writeStoredPageSize(value: PageSizeOption): void {
  try {
    localStorage.setItem(PAGE_SIZE_KEY, String(value));
  } catch {
    // ignore quota / private mode
  }
}

export function clampUiZoom(value: number): number {
  if (!Number.isFinite(value)) return UI_ZOOM_DEFAULT;
  const stepped = Math.round(value / UI_ZOOM_STEP) * UI_ZOOM_STEP;
  return Math.min(UI_ZOOM_MAX, Math.max(UI_ZOOM_MIN, Number(stepped.toFixed(1))));
}

export function readStoredUiZoom(fallback = UI_ZOOM_DEFAULT): number {
  try {
    const raw = Number(localStorage.getItem(UI_ZOOM_KEY));
    return Number.isFinite(raw) ? clampUiZoom(raw) : fallback;
  } catch {
    return fallback;
  }
}

export function writeStoredUiZoom(value: number): void {
  try {
    localStorage.setItem(UI_ZOOM_KEY, String(clampUiZoom(value)));
  } catch {
    // ignore
  }
}

export function applyUiZoom(value: number): number {
  const zoom = clampUiZoom(value);
  document.documentElement.style.zoom = String(zoom);
  writeStoredUiZoom(zoom);
  return zoom;
}

/** VS Code-compatible zoom shortcuts (Ctrl/Cmd + = / - / 0). */
export function matchZoomShortcut(event: KeyboardEvent): "in" | "out" | "reset" | null {
  if (!(event.ctrlKey || event.metaKey) || event.altKey) return null;
  if (event.key === "=" || event.key === "+" || event.code === "NumpadAdd") return "in";
  if (event.key === "-" || event.key === "_" || event.code === "NumpadSubtract") return "out";
  if (event.key === "0" || event.code === "Numpad0") return "reset";
  return null;
}
